/**
 * Currency detection service.
 * Determines whether to show EUR or USD based on user's timezone.
 * 
 * Logic: If the user's timezone is in a European country → EUR
 *        Otherwise (USA, LATAM, Asia, etc.) → USD
 */

export type CurrencyCode = 'EUR' | 'USD';

// European timezones (covers EU + EEA + UK + Switzerland + nearby)
const EUROPEAN_TIMEZONES = new Set([
    // Western Europe
    'Europe/London', 'Europe/Dublin', 'Europe/Lisbon',
    // Central Europe
    'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome',
    'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna', 'Europe/Zurich',
    'Europe/Prague', 'Europe/Warsaw', 'Europe/Budapest', 'Europe/Copenhagen',
    'Europe/Stockholm', 'Europe/Oslo', 'Europe/Helsinki',
    'Europe/Luxembourg', 'Europe/Monaco', 'Europe/Andorra',
    // Eastern Europe
    'Europe/Bucharest', 'Europe/Sofia', 'Europe/Athens', 'Europe/Zagreb',
    'Europe/Ljubljana', 'Europe/Bratislava', 'Europe/Tallinn',
    'Europe/Riga', 'Europe/Vilnius', 'Europe/Malta',
    // Other European
    'Europe/Belgrade', 'Europe/Sarajevo', 'Europe/Podgorica',
    'Europe/Skopje', 'Europe/Tirane', 'Europe/Chisinau',
    'Europe/Kiev', 'Europe/Kyiv', 'Europe/Minsk',
    'Europe/Moscow', 'Europe/Istanbul',
    // Atlantic islands (EU territories)
    'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores',
    'Atlantic/Reykjavik',
]);

/**
 * Detect user currency based on timezone.
 * Cached after first call for consistency within session.
 */
let cachedCurrency: CurrencyCode | null = null;

export const getUserCurrency = (): CurrencyCode => {
    if (cachedCurrency) return cachedCurrency;

    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        cachedCurrency = EUROPEAN_TIMEZONES.has(tz) ? 'EUR' : 'USD';
    } catch {
        // Fallback: check navigator.language for European locales
        const lang = navigator.language || '';
        const euroLocales = ['es-ES', 'fr-FR', 'de-DE', 'it-IT', 'pt-PT', 'nl-NL', 'pl-PL', 'ro-RO', 'el-GR', 'cs-CZ', 'hu-HU', 'sv-SE', 'da-DK', 'fi-FI', 'sk-SK', 'bg-BG', 'hr-HR', 'sl-SI', 'et-EE', 'lv-LV', 'lt-LT', 'mt-MT', 'ga-IE', 'en-GB', 'en-IE'];
        cachedCurrency = euroLocales.some(l => lang.startsWith(l)) ? 'EUR' : 'USD';
    }

    return cachedCurrency;
};

/** Returns '€' or '$' */
export const getCurrencySymbol = (): string => {
    return getUserCurrency() === 'EUR' ? '€' : '$';
};

/** Returns true if user should see EUR pricing */
export const isEuropean = (): boolean => {
    return getUserCurrency() === 'EUR';
};
